import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../lib/apiClient";
import { ig, igGlobalStyles, Badge } from "../inbox/_inbox_shared";
import {
  Search, Sparkles, Folder, Plus, RefreshCw, Filter, ArrowUpDown,
  Settings as Cog, ListFilter, MoreHorizontal, ChevronRight,
} from "lucide-react";
import BuildWithAIModal from "./BuildWithAIModal";

const STATUS_TONES = { draft: "neutral", published: "success", paused: "warn",
                        needs_review: "warn", error: "error", archived: "neutral" };

export default function WorkflowsList() {
  const nav = useNavigate();
  const [tab, setTab] = useState("workflows");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [aiOpen, setAiOpen] = useState(false);

  const load = () => api.get("/api/automation/workflows" + (q ? `?q=${encodeURIComponent(q)}` : ""))
    .then((d) => setItems(d.items)).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const filtered = (items || []).filter((w) =>
    filter === "all" ? true : filter === "needs_review" ? w.status === "needs_review" : true
  );

  const createBlank = async () => {
    const r = await api.post("/api/automation/workflows", { name: "Untitled Workflow", nodes: [], edges: [] });
    nav(`/admin/automation/${r.id}`);
  };

  return (
    <div data-testid="workflows-list" style={{
      background: ig.bg, color: ig.ink, minHeight: "calc(100vh - 0px)",
      margin: "-22px -26px -80px", padding: "20px 26px",
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    }}>
      <style>{igGlobalStyles}</style>

      {/* Header tabs */}
      <div style={{ display: "flex", alignItems: "center", gap: 22, borderBottom: `1px solid ${ig.border}`, paddingBottom: 0 }}>
        <h1 style={{ fontSize: 16, margin: "0 10px 0 0", fontWeight: 800 }}>Automation</h1>
        {[["workflows","Workflows"],["overview","Overview"],["global","Global Workflow Settings"]].map(([k, label], i) => (
          <button key={k} data-testid={`auto-tab-${k}`} onClick={() => setTab(k)} style={{
            padding: "12px 0", border: 0, background: "transparent", cursor: "pointer",
            color: tab === k ? "#2563EB" : ig.muted, fontWeight: 700, fontSize: 13,
            borderBottom: "2px solid " + (tab === k ? "#22C55E" : "transparent"),
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            {label}{label === "Overview" && <Badge tone="info" style={{ marginLeft: 4 }}>Beta</Badge>}
          </button>
        ))}
      </div>

      {tab !== "workflows" ? (
        <Placeholder tab={tab} />
      ) : (
        <>
          {/* Title + action buttons */}
          <div style={{ display: "flex", alignItems: "center", marginTop: 26 }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, margin: 0, flex: 1 }}>Workflows list</h2>
            <button data-testid="create-folder" style={btnSecondary}><Folder size={14} /> Create folder</button>
            <button data-testid="build-using-ai" onClick={() => setAiOpen(true)} style={{ ...btnSecondary, color: "#7C3AED", borderColor: "#DDD6FE", marginLeft: 8 }}>
              <Sparkles size={14} /> Build using AI
            </button>
            <button data-testid="create-workflow" onClick={createBlank} style={btnPrimary}><Plus size={14} /> Create workflow</button>
          </div>

          {/* Filters row */}
          <div style={{ display: "flex", alignItems: "center", marginTop: 22, borderBottom: `1px solid ${ig.border}`, paddingBottom: 8 }}>
            {[["all","All workflows"],["needs_review","Needs review (0)"],["deleted","Deleted"]].map(([k, l]) => (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} style={{
                padding: "8px 14px", border: 0, background: "transparent", cursor: "pointer",
                color: filter === k ? "#2563EB" : ig.muted, fontWeight: 700, fontSize: 13,
                borderBottom: "2px solid " + (filter === k ? "#2563EB" : "transparent"),
              }}>{l}</button>
            ))}
            <button data-testid="new-smart-list" style={{ ...btnGhost, marginLeft: 4 }}><Plus size={13} /> New smart list</button>
            <div style={{ flex: 1 }} />
            <button data-testid="customize-list" style={btnGhost}><ListFilter size={13} /> Customize list</button>
          </div>

          {/* Search + controls */}
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <button data-testid="advanced-filters" style={{ ...btnGhost, borderRadius: 999 }}>
              <Filter size={12} /> Advanced filters
            </button>
            <div style={{ flex: 1 }} />
            <button data-testid="stats-view" style={iconBtn}><Cog size={14} /></button>
            <button data-testid="last-updated" style={iconBtn}><RefreshCw size={14} /></button>
            <button data-testid="customize" style={{ ...iconBtn, background: "#F1F5F4" }}><ArrowUpDown size={14} /></button>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: ig.muted }} />
              <input data-testid="workflow-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                style={{ height: 36, padding: "0 12px 0 32px", borderRadius: 999, border: `1px solid ${ig.border}`,
                  background: ig.card, fontSize: 12.5, width: 200, outline: "none", color: ig.ink }} />
            </div>
          </div>

          {/* Folder breadcrumb */}
          <div style={{ marginTop: 18, fontSize: 13 }}>
            <span style={{ color: ig.muted, cursor: "pointer" }}>Back</span>
            <span style={{ color: ig.muted, margin: "0 8px" }}>/</span>
            <span style={{ fontWeight: 800 }}>Marketing Workflows</span>
          </div>

          {/* Table */}
          <div style={{ marginTop: 14, background: ig.card, borderRadius: 14,
            border: `1px solid ${ig.border}`, boxShadow: ig.shadow, overflow: "hidden" }}>
            <div style={headerRow}>
              <div style={{ width: 32 }}><input type="checkbox" /></div>
              <div style={{ flex: 1.6 }}>Name</div>
              <div style={{ flex: 0.7 }}>Status</div>
              <div style={{ flex: 0.7 }}>Total enrolled</div>
              <div style={{ flex: 0.7 }}>Active enrolled</div>
              <div style={{ flex: 0.9 }}>Last updated</div>
              <div style={{ flex: 0.9 }}>Created on</div>
              <div style={{ flex: 0.5, display: "flex", alignItems: "center", gap: 4 }}>Stats ⓘ</div>
              <div style={{ width: 32 }} />
            </div>
            {!items ? <div style={{ padding: 40, textAlign: "center", color: ig.muted }}>Loading…</div>
              : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: ig.muted }}>No workflows yet.</div>
            ) : filtered.map((w) => (
              <div key={w.id} data-testid={`wf-row-${w.id}`} onClick={() => nav(`/admin/automation/${w.id}`)} style={{
                ...rowStyle, cursor: "pointer",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#FAFCFB"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                <div style={{ width: 32 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" /></div>
                <div style={{ flex: 1.6, fontWeight: 600, color: ig.ink, display: "flex", alignItems: "center", gap: 8 }}>
                  {w.name} <ChevronRight size={12} color={ig.muted} />
                </div>
                <div style={{ flex: 0.7 }}>
                  <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: STATUS_TONES[w.status] === "success" ? "#DCFCE7" : "#F1F5F4",
                    color: STATUS_TONES[w.status] === "success" ? "#15803D" : "#475569",
                    border: `1px solid ${STATUS_TONES[w.status] === "success" ? "#A7F3D0" : "#E2E8F0"}` }}>
                    {w.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div style={{ flex: 0.7, color: "#2563EB", fontWeight: 600 }}>{w.stats?.total_enrolled ?? 0}</div>
                <div style={{ flex: 0.7, color: "#2563EB", fontWeight: 600 }}>{w.stats?.active_enrolled ?? 0}</div>
                <div style={{ flex: 0.9, color: ig.muted, fontSize: 11.5 }}>{niceDate(w.updated_at)}</div>
                <div style={{ flex: 0.9, color: ig.muted, fontSize: 11.5 }}>{niceDate(w.created_at)}</div>
                <div style={{ flex: 0.5 }}><ChevronRight size={14} color={ig.muted} /></div>
                <div style={{ width: 32, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                  <button style={iconBtn}><MoreHorizontal size={14} /></button>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "10px 16px",
              borderTop: `1px solid ${ig.borderSoft}`, fontSize: 12, color: ig.muted, gap: 8 }}>
              <button style={btnGhost}>Previous</button>
              <span style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid #BFDBFE`, color: "#2563EB", fontWeight: 700 }}>1</span>
              <button style={btnGhost}>Next</button>
              <span>10 / page</span>
            </div>
          </div>
        </>
      )}

      {aiOpen && <BuildWithAIModal onClose={() => setAiOpen(false)} onCreated={(wid) => nav(`/admin/automation/${wid}`)} />}
    </div>
  );
}

function Placeholder({ tab }) {
  return (
    <div style={{ marginTop: 80, textAlign: "center", color: ig.muted }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: ig.ink }}>
        {tab === "overview" ? "Overview · workflow performance" : "Global workflow settings"}
      </div>
      <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.55, maxWidth: 460, margin: "8px auto 0" }}>
        {tab === "overview"
          ? "Cross-workflow KPIs: enrollment volume, completion rate, AI handle rate, and error rates — ships in the next pass."
          : "Account-wide automation defaults: business hours, AI tone, DND enforcement, retry policy — ships in the next pass."}
      </div>
      <Badge tone="warn" style={{ marginTop: 14 }}>Coming next phase</Badge>
    </div>
  );
}

function niceDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

const btnSecondary = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px",
  border: `1px solid ${ig.border}`, borderRadius: 10, background: ig.card, color: ig.ink,
  fontSize: 13, fontWeight: 700, cursor: "pointer",
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", border: 0, borderRadius: 10,
  background: "#2563EB", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)", marginLeft: 10,
};
const btnGhost = {
  display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7,
  border: `1px solid ${ig.border}`, background: ig.card, color: ig.muted, fontSize: 12, fontWeight: 600, cursor: "pointer",
};
const iconBtn = {
  width: 36, height: 36, borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card,
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.ink,
};
const headerRow = {
  display: "flex", alignItems: "center", gap: 8, padding: "12px 16px",
  background: "#FAFCFB", color: ig.muted, fontSize: 11.5, fontWeight: 700,
  borderBottom: `1px solid ${ig.borderSoft}`, letterSpacing: 0.3,
};
const rowStyle = {
  display: "flex", alignItems: "center", gap: 8, padding: "14px 16px",
  borderBottom: `1px solid ${ig.borderSoft}`, fontSize: 13.5,
};
